import { get } from 'lodash';
import { Collection } from '../types/database.type';
import { KeyOf } from '../types/nestedKey.type';

/**
 * Various functions to manipulate maps
 */
export class MapHlp {
  /**
   * Create map from object list by key.
   * ! When child key is of nullable object, use 'as any' with path to omit compilation errors.
   * @param {T[]}      list
   * @param {KeyOf<T>} path path to key, default 'id'
   * @return {Map<string, T>}
   */
  public static idMap<T>(list: T[], path?: KeyOf<T>): Map<string, T> {
    const map = new Map<string, T>();
    if (Array.isArray(list)) list.forEach((x: any) => map.set(get(x, path ?? 'id'), x));
    return map;
  }

  /**
   * Create two-field map from object list
   * @param {T[]}      list
   * @param {KeyOf<T>} mapKey path to field which will be key of map
   * @return {Map<string, U>}
   */
  public static fieldsMap<T>(list: T[], mapKey: KeyOf<T>): Map<string, T>;

  /**
   * Create two-field map from object list
   * @param {T[]}      list
   * @param {KeyOf<T>} mapKey   path to field which will be key of map
   * @param {KeyOf<T>} fieldKey path to field which will be value of map
   * @return {Map<string, U>}
   */
  public static fieldsMap<T, U extends T[keyof T]>(list: T[], mapKey: KeyOf<T>, fieldKey: KeyOf<T>): Map<string, U>;

  /**
   * Create two-field map from object list
   * @param {T[]}      list
   * @param {KeyOf<T>} mapKey
   * @param {function} fieldParser parser from object to value of map; if undefined, take whole object
   * @return {Map<string, U>}
   */
  public static fieldsMap<T, U>(list: T[], mapKey: KeyOf<T>, fieldParser: (obj: T) => U): Map<string, U>;

  // Implementation
  // eslint-disable-next-line require-jsdoc
  public static fieldsMap<T, U>(list: T[], mapKey: KeyOf<T>, fieldGetter?: ((obj: T) => U) | KeyOf<T>): Map<string, U> {
    let getFieldFunc: (x: T) => any = (x: T) => x;
    if (typeof fieldGetter === 'function') getFieldFunc = fieldGetter;
    else if (fieldGetter) getFieldFunc = (x) => get(x, fieldGetter);

    const map = new Map<string, U>();
    if (Array.isArray(list)) list.forEach((x: any) => map.set(get(x, mapKey), getFieldFunc(x)));

    return map;
  }

  /**
   * Create map from object list by keys /
   * ! For nested objects - When child key is of nullable object, use 'as any[]' with paths to omit compilation errors
   * @param {T[]}      list
   * @param {string[]} paths default ['id']
   * @return {Map<string, T>}
   */
  public static idMapArray<T>(list: T[], paths: string[] = ['id']): Map<string, T[]> {
    const map = new Map<string, T[]>();
    if (Array.isArray(list)) {
      list.forEach((x: any) => {
        // ? path type to make compiler shut up
        paths.forEach((path) => {
          const k = get(x, path);
          if (k) MapHlp.arrayMapAdd(map, k, x);
        });
      });
    }
    return map;
  }

  /**
   * Add elements into map of arrays
   * @param {Map<K, T[]>} map
   * @param {K}           key
   * @param {T[]}         value
   * @return {boolean} true if new key was created
   */
  public static arrayMapAdd<K, T>(map: Map<K, T[]>, key: K, ...value: T[]): boolean {
    const hasKey = map.has(key);
    if (hasKey) map.get(key)!.push(...value);
    else map.set(key, value);
    return !hasKey;
  }

  /**
   * Add elements into map of objects
   * @param {Map<K, Collection<T>>} map
   * @param {K}                     key
   * @param {Collection<T>}         value aka {[key: string]: T}
   * @return {boolean} true if new key was created
   */
  public static objectMapAdd<K, T>(map: Map<K, Collection<T>>, key: K, value: Collection<T>): boolean {
    const hasKey = map.has(key);
    if (hasKey) value = { ...map.get(key)!, ...value };
    map.set(key, value);
    return !hasKey;
  }

  // TODO: tests
  /**
   * Add single element into map of arrays
   * @param {Map<K, T[]>} map
   * @param {K}           key
   * @param {T}           value
   * @return {boolean} true if new key was created
   */
  public static setMapAdd<K, T>(map: Map<K, Set<T>>, key: K, value: T): boolean {
    const hasKey = map.has(key);
    if (hasKey) map.get(key)!.add(value);
    else map.set(key, new Set([value]));
    return !hasKey;
  }

  /**
   * Returns element of map or undefined if key or value don't exist
   * @param {Map<K, T[]>} map
   * @param {K}           key
   * @return {T | undefined} map element if key and value exists
   */
  public static get<K, T>(map: Map<K, T>, key: K | null | undefined): T | undefined {
    return key ? map.get(key) : undefined;
  }

  public static getSet<K, T>(map: Map<K, T>, key: K, template: T): T;
  public static getSet<K, T>(map: Map<K, T>, key: K, template: () => T): T;

  /**
   * Get element of map, set template if not found
   * @param {Map<K, T[]>} map
   * @param {K}           key
   * @param {T}           template
   * @return {T} desired element if found, else template
   */
  public static getSet<K, T>(map: Map<K, T>, key: K, template: T | (() => T)): T {
    let elem = map.get(key);
    if (!elem) {
      elem = typeof template === 'function' ? (template as () => T)() : template;
      map.set(key, elem);
    }
    return elem;
  }

  /**
   * Get element of map, removing it from map
   * @param {Map<K, T[]>} map
   * @param {K}           key
   * @return {T} desired element if found, else undefined
   */
  public static pop<K, T>(map: Map<K, T>, key: K): T | undefined {
    const elem = map.get(key);
    if (elem) map.delete(key);
    return elem;
  }

  // TODO tests
  /**
   * Calls the specified callback function for all the elements in an map. The return value of the callback function is the
   * accumulated result, and is provided as an argument in the next call to the callback function.
   * @param {Map}      map
   * @param {function} callback
   * @param {T}        initialValue
   * @return {T}
   */
  public static reduce<T, K, V>(map: Map<K, V>, callback: (acc: T, value: V, key: K) => T, initialValue: T): T {
    let accumulator = initialValue;
    map.forEach((value, key) => (accumulator = callback(accumulator, value, key)));
    return accumulator;
  }

  // TODO: tests
  /**
   * Set element of map using previous value of element
   * @param {Map}      map
   * @param {K}        key
   * @param {function} callback takes old value, return new, undefined means to delete element
   * @return {T | undefined} new set value
   */
  public static update<K, T>(map: Map<K, T>, key: K, callback: (value: T | undefined, key: K) => T | undefined): T | undefined {
    const value = map.get(key);
    const newValue = callback(value, key);
    if (newValue !== undefined) map.set(key, newValue);
    else map.delete(key);
    return newValue;
  }

  // TODO: tests
  /**
   * Join multiple maps or list of entries into one
   * @param {(Map<K, T> | [K, T][])[]} toJoin maps to join
   * @return {Map<K, T>}
   */
  public static join<K, T>(...toJoin: (Map<K, T> | [K, T][])[]): Map<K, T> {
    const joined = new Map<K, T>();
    toJoin.forEach((map) => {
      if (Array.isArray(map)) {
        map.forEach((entry) => joined.set(entry[0], entry[1]));
      } else {
        map.forEach((value, key) => joined.set(key, value));
      }
    });
    return new Map(joined);
  }

  /**
   * Jsonify map - it also checks for nested maps
   * @param {Map<K, any>} map
   * @param {number}      space Add indentation to return-value json (number of spaces)
   * @return {string} jsonified map
   */
  public static toJson<K extends string>(map: Map<K, any>, space?: number): string {
    const obj: any = {};
    map.forEach((value, key) => {
      if (value instanceof Map) obj[key] = this.toObject(value);
      else obj[key] = value;
    });
    return JSON.stringify(obj, undefined, space);
  }

  /**
   * Map to object - it also checks for nested maps
   * @param {Map<K, any>} map
   * @return {any}
   */
  public static toObject<K extends string>(map: Map<K, any>): { [key in K]: any } {
    const obj: any = {};
    map.forEach((value, key) => {
      if (value instanceof Map) obj[key] = this.toObject(value);
      else obj[key] = value;
    });
    return obj;
  }

  /**
   * Create map from object or Collection
   * @param {any | Collection<T>} object
   * @return {Map<string, T>}
   */
  public static fromObject<T>(object: any | Collection<T>): Map<string, T> {
    if (typeof object == 'object' && object != null) return new Map<string, T>(Object.entries(object));
    else return new Map();
  }

  /**
   * Returns copy of map including only entries fitting predicate
   * @param {Map<K,V>} map
   * @param {function} predicate
   * @return {Map<K,V>}
   */
  public static filter<K, V>(map: Map<K, V>, predicate: (value: V, key: K, map: Map<K, V>) => boolean) {
    const filtered = new Map<K, V>();
    map.forEach((v, k, m) => {
      if (predicate(v, k, m)) filtered.set(k, v);
    });
    return filtered;
  }
}
